import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { Navbar } from "@/components/Navbar";
import { Calendar, Briefcase, ArrowRight } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function RoleSelect() {
  const { setUser } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const selectRole = async (role) => {
    setLoading(true);
    try {
      const response = await axios.put(`${API}/users/role`, { role }, { withCredentials: true });
      setUser(response.data);
      if (role === "business") navigate("/business/register", { replace: true });
      else navigate("/customer", { replace: true });
    } catch {
      toast.error(t("common.error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Navbar />
      <div className="pt-32 pb-20 px-6 lg:px-16">
        <div className="max-w-2xl mx-auto">
          <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tight text-[#121212] mb-16" data-testid="role-title">
            {t("role.title")}
          </h1>
          <div className="grid gap-6">
            {/* Customer Card - Light/Green accent */}
            <button
              data-testid="role-customer-button"
              onClick={() => selectRole("customer")}
              disabled={loading}
              className="group p-8 border-2 border-[#E5E7EB] bg-white text-left hover:border-emerald-500 transition-all duration-200 disabled:opacity-50"
            >
              <div className="flex items-start gap-6">
                <div className="w-14 h-14 bg-emerald-50 border border-emerald-200 flex items-center justify-center shrink-0">
                  <Calendar className="h-7 w-7 text-emerald-600" strokeWidth={1.5} />
                </div>
                <div className="flex-1">
                  <h3 className="font-heading text-xl font-bold text-[#121212] mb-2">
                    {t("role.customer")}
                  </h3>
                  <p className="text-sm text-[#52525B] leading-relaxed">{t("role.customer.desc")}</p>
                  <ul className="mt-4 space-y-1.5 text-xs text-[#52525B]">
                    <li className="flex items-center gap-2">
                      <span className="w-1 h-1 bg-emerald-500 shrink-0" />
                      {t("landing.path.customer.f1")}
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-1 h-1 bg-emerald-500 shrink-0" />
                      {t("landing.path.customer.f2")}
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-1 h-1 bg-emerald-500 shrink-0" />
                      {t("landing.path.customer.f3")}
                    </li>
                  </ul>
                </div>
                <ArrowRight className="h-5 w-5 text-emerald-600 opacity-0 group-hover:opacity-100 transition-opacity duration-200 mt-2" />
              </div>
            </button>

            {/* Business Card - Dark/Blue accent */}
            <button
              data-testid="role-business-button"
              onClick={() => selectRole("business")}
              disabled={loading}
              className="group p-8 border-2 border-[#1A1A1A] bg-[#1A1A1A] text-left hover:bg-[#222] transition-all duration-200 disabled:opacity-50"
            >
              <div className="flex items-start gap-6">
                <div className="w-14 h-14 bg-[#0047AB]/20 border border-[#0047AB]/40 flex items-center justify-center shrink-0">
                  <Briefcase className="h-7 w-7 text-[#0047AB]" strokeWidth={1.5} />
                </div>
                <div className="flex-1">
                  <h3 className="font-heading text-xl font-bold text-white mb-2">
                    {t("role.business")}
                  </h3>
                  <p className="text-sm text-gray-400 leading-relaxed">{t("role.business.desc")}</p>
                  <ul className="mt-4 space-y-1.5 text-xs text-gray-400">
                    <li className="flex items-center gap-2">
                      <span className="w-1 h-1 bg-[#0047AB] shrink-0" />
                      {t("landing.path.business.f1")}
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-1 h-1 bg-[#0047AB] shrink-0" />
                      {t("landing.path.business.f2")}
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-1 h-1 bg-[#0047AB] shrink-0" />
                      {t("landing.path.business.f3")}
                    </li>
                  </ul>
                </div>
                <ArrowRight className="h-5 w-5 text-[#0047AB] opacity-0 group-hover:opacity-100 transition-opacity duration-200 mt-2" />
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
